import { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, useWatch } from "react-hook-form";
import Drawer from "@mui/material/Drawer";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  SIDE_DRAWER_UI,
  sideDrawerBackdropSx,
  sideDrawerPaperSx,
  sideDrawerRootSx,
} from "common/Components/SideDrawer/SideDrawer.constants";
import {
  CancelButton,
  DrawerBody,
  DrawerCloseButton,
  DrawerCloseIcon,
  DrawerFooter,
  DrawerHeader,
  DrawerShell,
  DrawerTitle,
  SubmitButton,
} from "common/Components/SideDrawer/SideDrawer.styles";
import { GenericForm } from "common/Components/GenericForm";
import GROUPS_CONSTANTS, {
  CUSTOMERS_PAGE_CONSTANTS,
  GROUP_ID_TYPES,
} from "./Customers.constants";
import {
  getGroupIdValidationError,
  GROUP_ID_MAX_LENGTH,
} from "./groupIdValidation.util";
import { useCustomerExistsCheck } from "./hooks/useCustomerExistsCheck";
import { FieldBlock } from "styles/formStyleComponents";
import { FieldsStack } from "./AddCustomerDrawer.styles";

const EMPTY_FORM = {
  name: "",
  timeZone: "",
  customerId: "",
};

const timeZoneOptions = INFRAMARK_CONSTANTS.groups.dropdown.timeZone;
const addCustomerFormFields = [
  {
    name: "name",
    label: GROUPS_CONSTANTS.createGroupLables.CUSTOMER_NAME,
    type: "text",
    required: true,
    placeholder: CUSTOMERS_PAGE_CONSTANTS.ADD_CUSTOMER_NAME_PLACEHOLDER,
  },
  {
    name: "customerId",
    label: GROUPS_CONSTANTS.createGroupLables.GROUP_ID,
    type: "text",
    required: true,
    placeholder: CUSTOMERS_PAGE_CONSTANTS.ADD_CUSTOMER_ID_PLACEHOLDER,
    validation: { maxLength: GROUP_ID_MAX_LENGTH },
  },
  {
    name: "timeZone",
    label: GROUPS_CONSTANTS.createGroupLables.TIME_ZONE,
    type: "singleSelect",
    required: true,
    placeholder: CUSTOMERS_PAGE_CONSTANTS.ADD_CUSTOMER_TIMEZONE_PLACEHOLDER,
    options: timeZoneOptions,
    disableClearable: true,
  },
];

const AddCustomerDrawer = ({
  open,
  onClose,
  groups = [],
  onSubmit,
  checkCustomerExists,
  saving = false,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
    setValue,
    setError,
    clearErrors,
    reset,
  } = useForm({
    defaultValues: EMPTY_FORM,
  });

  const formValues = useWatch({ control, defaultValue: EMPTY_FORM });

  const {
    checkingName,
    checkingCode,
    checkNameOnBlur,
    checkCodeOnBlur,
  } = useCustomerExistsCheck({ checkCustomerExists });

  useEffect(() => {
    if (!open) return;
    reset(EMPTY_FORM);
    clearErrors();
  }, [open, reset, clearErrors]);

  const handleWhiteSpaces = (event) => {
    if (event.key === " " || event.keyCode === 32) {
      event.preventDefault();
    }
  };

  const setFieldManualError = useCallback(
    (fieldName, message) => {
      setError(fieldName, {
        type: GROUPS_CONSTANTS.createGroup.ERROR_TYPE_MANUAL,
        message,
      });
    },
    [setError]
  );

  const validateName = (val) => {
    const trimmedVal = val?.trim() || "";
    let errorMessage = null;

    if (!trimmedVal) {
      errorMessage = GROUPS_CONSTANTS.createGroupErrorMessages.nameRequired;
    } else if (
      !checkCustomerExists &&
      groups?.some((g) => g.name?.toLowerCase() === trimmedVal.toLowerCase())
    ) {
      errorMessage = GROUPS_CONSTANTS.createGroupErrorMessages.name;
    }

    if (errorMessage) {
      const errorType =
        errorMessage === GROUPS_CONSTANTS.createGroupErrorMessages.nameRequired
          ? GROUPS_CONSTANTS.createGroup.ERROR_TYPE_REQUIRED
          : GROUPS_CONSTANTS.createGroup.ERROR_TYPE_MANUAL;
      setError("name", {
        type: errorType,
        message: errorMessage,
      });
      return errorMessage;
    }

    clearErrors("name");
    return true;
  };

  const validateCustomerId = (val) => {
    const errorMessage = getGroupIdValidationError(
      val,
      groups,
      GROUP_ID_TYPES.customerId
    );
    const duplicateMessage =
      GROUPS_CONSTANTS.createGroupErrorMessages.idDuplicate.replace(
        "{idType}",
        GROUP_ID_TYPES.customerId
      );

    if (checkCustomerExists && errorMessage === duplicateMessage) {
      clearErrors("customerId");
      return true;
    }

    if (errorMessage) {
      const requiredMessage =
        GROUPS_CONSTANTS.createGroupErrorMessages.idRequired.replace(
          "{idType}",
          GROUP_ID_TYPES.customerId
        );
      const errorType =
        errorMessage === requiredMessage
          ? GROUPS_CONSTANTS.createGroup.ERROR_TYPE_REQUIRED
          : GROUPS_CONSTANTS.createGroup.ERROR_TYPE_MANUAL;
      setError("customerId", {
        type: errorType,
        message: errorMessage,
      });
      return errorMessage;
    }

    clearErrors("customerId");
    return true;
  };

  const validateTimeZone = (val) => {
    if (!val || val === "") {
      setError("timeZone", {
        type: GROUPS_CONSTANTS.createGroup.ERROR_TYPE_REQUIRED,
        message: GROUPS_CONSTANTS.createGroupErrorMessages.timeZoneRequired,
      });
      return GROUPS_CONSTANTS.createGroupErrorMessages.timeZoneRequired;
    }
    clearErrors("timeZone");
    return true;
  };

  register("name", {
    required: GROUPS_CONSTANTS.createGroupErrorMessages.nameRequired,
    validate: validateName,
  });
  register("customerId", {
    required: GROUPS_CONSTANTS.createGroupErrorMessages.idRequired.replace(
      "{idType}",
      GROUP_ID_TYPES.customerId
    ),
    validate: validateCustomerId,
  });
  register("timeZone", {
    required: GROUPS_CONSTANTS.createGroupErrorMessages.timeZoneRequired,
    validate: validateTimeZone,
  });

  const fieldValidators = {
    name: validateName,
    customerId: validateCustomerId,
    timeZone: validateTimeZone,
  };

  const handleFieldChange = (name, value) => {
    setValue(name, value);
    fieldValidators[name]?.(value);
  };

  const resolveExistsErrorField = (fieldName, result) => {
    if (result.field === "code") {
      return CUSTOMERS_PAGE_CONSTANTS.CUSTOMER_ID_FIELD;
    }
    if (result.field === "name") {
      return CUSTOMERS_PAGE_CONSTANTS.NAME_FIELD;
    }
    return fieldName;
  };

  const applyExistsCheckResult = (fieldName, result) => {
    const errorField = resolveExistsErrorField(fieldName, result);
    if (result.fieldError) {
      setFieldManualError(errorField, result.fieldError);
      return false;
    }
    if (!result.exists) {
      clearErrors(errorField);
    }
    return true;
  };

  const handleFieldBlur = async (name, value) => {
    const localResult = fieldValidators[name]?.(value);
    if (localResult !== true) {
      return;
    }
    if (name === CUSTOMERS_PAGE_CONSTANTS.NAME_FIELD) {
      const result = await checkNameOnBlur(value);
      applyExistsCheckResult(CUSTOMERS_PAGE_CONSTANTS.NAME_FIELD, result);
      return;
    }
    if (name === CUSTOMERS_PAGE_CONSTANTS.CUSTOMER_ID_FIELD) {
      const result = await checkCodeOnBlur(value);
      applyExistsCheckResult(CUSTOMERS_PAGE_CONSTANTS.CUSTOMER_ID_FIELD, result);
    }
  };

  const handleClose = () => {
    reset(EMPTY_FORM);
    clearErrors();
    onClose();
  };

  const verifyExistsBeforeSubmit = async (name, customerId) => {
    if (typeof checkCustomerExists !== "function") {
      return true;
    }

    const nameResult = await checkNameOnBlur(name);
    const codeResult = await checkCodeOnBlur(customerId);

    const nameOk = applyExistsCheckResult(
      CUSTOMERS_PAGE_CONSTANTS.NAME_FIELD,
      nameResult
    );
    const codeOk = applyExistsCheckResult(
      CUSTOMERS_PAGE_CONSTANTS.CUSTOMER_ID_FIELD,
      codeResult
    );

    return nameOk && codeOk;
  };

  const submitForm = async (data) => {
    const trimmedName = String(data.name ?? "").trim();
    const trimmedCode = String(data.customerId ?? "").trim();
    const canSubmit = await verifyExistsBeforeSubmit(trimmedName, trimmedCode);
    if (!canSubmit) {
      return;
    }

    const { customerId: code, ...restData } = data;
    onSubmit?.({ ...restData, name: trimmedName, code: trimmedCode }, () => {
      reset(EMPTY_FORM);
    });
  };

  const savingOrChecking = saving || checkingName || checkingCode;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={sideDrawerRootSx}
      slotProps={{
        backdrop: { sx: sideDrawerBackdropSx },
      }}
      PaperProps={{ sx: sideDrawerPaperSx }}
      ModalProps={{ keepMounted: false }}
    >
      <DrawerShell
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-customer-drawer-title"
      >
        <DrawerHeader>
          <DrawerTitle id="add-customer-drawer-title" component="h2">
            {CUSTOMERS_PAGE_CONSTANTS.ADD_CUSTOMER_DRAWER_TITLE}
          </DrawerTitle>
          <DrawerCloseButton
            type="button"
            aria-label={SIDE_DRAWER_UI.closeAriaLabel}
            title={SIDE_DRAWER_UI.closeAriaLabel}
            onClick={handleClose}
            disabled={savingOrChecking}
          >
            <DrawerCloseIcon />
          </DrawerCloseButton>
        </DrawerHeader>

        <DrawerBody>
          <FieldsStack component="form" id="add-customer-form" noValidate>
            <GenericForm
              fields={addCustomerFormFields}
              values={formValues}
              errors={{
                name: errors.name?.message,
                customerId: errors.customerId?.message,
                timeZone: errors.timeZone?.message,
              }}
              onChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
              showBorder={false}
              renderFieldWrapper={(field, fieldElement) => (
                <FieldBlock key={field.name} onKeyDown={handleWhiteSpaces}>
                  {fieldElement}
                </FieldBlock>
              )}
            />
          </FieldsStack>
        </DrawerBody>

        <DrawerFooter>
          <CancelButton
            type="button"
            onClick={handleClose}
            disabled={savingOrChecking}
          >
            {GROUPS_CONSTANTS.createGroupButtons.CANCEL}
          </CancelButton>
          <SubmitButton
            type="button"
            variant="contained"
            disabled={savingOrChecking}
            onClick={handleSubmit(submitForm)}
          >
            {GROUPS_CONSTANTS.createGroupButtons.SAVE}
          </SubmitButton>
        </DrawerFooter>
      </DrawerShell>
    </Drawer>
  );
};

AddCustomerDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  groups: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  checkCustomerExists: PropTypes.func,
  saving: PropTypes.bool,
};

AddCustomerDrawer.defaultProps = {
  groups: [],
  checkCustomerExists: undefined,
  saving: false,
};

export default AddCustomerDrawer;
